import type { LiteralUnion } from 'type-fest';

import type { DemoEnvironment, Environment, SubEnvironment } from '@change-corgi/config/environments';

const SHARED_TEST_KEY = 'pk_test_dOuclx4kw8wfviB5WEt73XG4';

// Sub-environments for which we have a dedicated Stripe sandbox account.
// Keys for canonical demo environments (jade, cyan, ...) come from the
// per-sandbox Stripe accounts. `preview` is reserved for future PR-preview deployments
type StripeKeyEnvironment = Environment | DemoEnvironment | 'test' | 'preview';

const STRIPE_KEYS = {
	production: 'pk_live_Xt6NLu4uUqbhU7arqU6xEdJT',
	staging: SHARED_TEST_KEY,
	demo: SHARED_TEST_KEY,
	development: SHARED_TEST_KEY,
	// `test` is not part of `Environment`, but Jest sets NODE_ENV=test and
	// some test setups read the key directly; keep it as a safe default.
	test: SHARED_TEST_KEY,
	cyan: 'pk_test_51TMGx42BpDqxt3dbfiSE1Wn6JbyHwx95oshU9MkpSCLaVGOgUmgsvIEla3TnuEwY6Ei9wzl6OnZjREkZ6SDr7Nc800fdoqzPXT',
	jade: 'pk_test_51TM8oGS5wBj0JKsHTlAMF1WeoeuUTl05JCNgwVB1VgBdqPSCTZMeAmbFbijvrogOaScA6gZ3lF7sXsfBipT2NHDR00nzqOTepW',
	noir: 'pk_test_51SNWHYS9e3mS8UaBBgRkjDwXacgQnIGrK8bFakO4p07OWU4GvLG3xVzOX8e60hlnn8l7x3hos4vRxzT7is6sHSFJ00qWIDxyb2',
	opal: 'pk_test_51TM8lJ27kXsyOn75KpYBmFka0T2gDZlj7molsZDQMu6zy2CrGP2rR77akP0g6pCFLkxL1CeB9LyuPu1hoXv3yaJl005r5IAyqz',
	orion: 'pk_test_51TMHluHHuKHmLFU03HyugthdUyluIiIR70Lqrep0GklCiBmNCbQ84GYB4pEndPp0Bqn2TnukH7Z5loCcurcLML7l00ohRftfay',
	plum: 'pk_test_51TMHcDQWxcw0XB0dMGc5RlViot3wvbG359Y6adgM8oF402sfKwI9lXhvXxYINKSLR4K5RLEgByUMgtPt5X7OKBP100GxGlJgmQ',
	preview:
		'pk_test_51TMHzVHOYzRbdJPiscQiUxODLXGee2xn7xlmTVIFq9CWu9JyirR4VIjyiNfgRx6TQVYP303ZhSD8DiFldozgRqSy00TpHf1zpb',
	teal: 'pk_test_51TMHzVHOYzRbdJPiscQiUxODLXGee2xn7xlmTVIFq9CWu9JyirR4VIjyiNfgRx6TQVYP303ZhSD8DiFldozgRqSy00TpHf1zpb',
	vert: 'pk_test_51TM8iTS2gMOmYuWOGjJUf02R3Jeb7E773VCQhF072H3odbFqQ1BtbPGU8NwkrwmG91EdiQN4O6kOm3Kv7YE4CnO600v9utwc7G',
} as const satisfies Readonly<Record<StripeKeyEnvironment, string>>;

function isStripeKeyEnvironment(environment: string): environment is StripeKeyEnvironment {
	return environment in STRIPE_KEYS;
}

type GetStripeKeyParams = Readonly<{
	environment: Environment;
	subEnvironment?: LiteralUnion<SubEnvironment, string> | undefined;
}>;

/**
 * Resolves the Stripe publishable key for a given environment.
 *
 * Resolution order:
 * 1. If `subEnvironment` matches a known sandbox (e.g. `cyan`, `jade`), return its key.
 * 2. Otherwise fall back to the base `environment` key.
 *
 * Callers responsible for handling personalized demo environments (e.g.
 * `jlou.changeeng.org` -> cyan) should pass `getDemoEnvironmentTarget()`
 * (or `getApiDemoEnvironmentTarget()`) as `subEnvironment`, falling back to
 * `getSubEnvironment()` so that canonical demo hosts still resolve correctly.
 */
export function getStripeKey({ environment, subEnvironment }: GetStripeKeyParams): string {
	if (subEnvironment && isStripeKeyEnvironment(subEnvironment)) {
		return STRIPE_KEYS[subEnvironment];
	}
	return STRIPE_KEYS[environment];
}
